import { useEffect, useState } from 'react';
import { setToken } from '../api.js';

/** Codes are 32 characters from an alphabet with no 0/O/1/I/l — see ids.ts. */
const CODE = /^[2-9A-HJ-NP-Z]{32}$/;
const clean = (raw: string) => raw.toUpperCase().replace(/[^2-9A-HJ-NP-Z]/g, '').slice(0, 32);

/** Shown in groups of four, because 32 unbroken characters is unreadable. */
const grouped = (code: string) => (code.match(/.{1,4}/g) ?? []).join(' ');

/**
 * How a child gets in.
 *
 * Two ways, because typing 32 characters on an iPad is miserable and a
 * six-year-old will not manage it:
 *
 *   1. A parent sends a link with ?code=… on it. The child taps it, and the code
 *      is stored and stripped from the address bar immediately so it does not
 *      sit in history or get shared by accident.
 *   2. Typing it, for when there is no way to send a link. Input is uppercased
 *      and filtered as it goes, and shown in groups of four.
 *
 * There is no password, no email, and no sign-up. A code comes from a parent or
 * it does not come at all.
 */
export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [code, setCode] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const fromLink = new URLSearchParams(window.location.search).get('code');
    if (!fromLink) return;
    const candidate = clean(fromLink);
    if (!CODE.test(candidate)) return;
    setToken(candidate);
    // Strip it before anything can record it.
    window.history.replaceState({}, '', window.location.pathname);
    onSignedIn();
  }, [onSignedIn]);

  const ready = CODE.test(code);

  return (
    <main className="shell shell--centred">
      <form
        className="signin"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          setToken(code);
          setFailed(false);
          onSignedIn();
        }}
      >
        <h1>Welcome to the Studio</h1>
        <p>Type the code a grown-up gave you.</p>
        <input
          className="signin__code"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          value={grouped(code)}
          placeholder="XXXX XXXX XXXX XXXX"
          onChange={(e) => {
            setCode(clean(e.target.value));
            setFailed(false);
          }}
        />
        <p className="signin__count">{code.length} of 32</p>
        <button className="go" disabled={!ready}>
          Let me in
        </button>
        {failed && <p className="signin__bad">That code didn’t work. Check it and try again.</p>}
      </form>
    </main>
  );
}
